import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { Box, Center, Spinner } from '@chakra-ui/react';
import FileUploadBox from 'src/features/DataUpload/components/organisms/FileUploadBox';
import { AnalyzingSpinner } from 'src/features/DataUpload/components/organisms/AnalyzingSpinner';
import DataVisualization from 'src/features/DataUpload/components/organisms/DataVisualization';
import { AnalysisState } from 'src/features/DataUpload/types';
import { captureException } from 'src/libs/sentry';
import { useAppContext } from 'src/contexts/AppContext';
import { UploadedDataResponse } from 'src/types';
import { customerDataUploadApiService } from 'src/api/customerDataUploadApiService';
import { invalidateCustomerDataQueries } from 'src/api/customerDataCache';
import { getUploadedFileName } from 'src/features/DataUpload/utils/uploadedFilesHelpers';
import { formatFailedUploadsMessage } from 'src/features/DataUpload/utils/uploadErrorHelpers';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { ConfirmDialog } from 'src/components/molecules/ConfirmDialog';
import {
  CustomerRole,
  UserRole,
  useAuthContext,
} from 'src/contexts/AuthContext';

type DataUploadViewProps = {
  // Reports whether the customer has confirmed uploaded data on the server
  // (used by the onboarding stepper to gate the Next button)
  onUploadedDataChange?: (hasUploadedData: boolean) => void;
};

// Single source of truth for what the data view shows: all of the
// customer's succeeded uploads — used by both the restore-on-mount path
// and the post-upload path so they can't drift apart
const fetchAllUploadedFiles = async (customerId: string) => {
  const { data } =
    await customerDataUploadApiService.getUploadedFiles(customerId);
  return {
    fileIds: data.map((file) => file.fileId),
    fileNames: data.map(getUploadedFileName),
  };
};

export const DataUploadView: React.FC<DataUploadViewProps> = ({
  onUploadedDataChange,
}) => {
  const [analysisState, setAnalysisState] = useState<AnalysisState>('idle');
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  const [uploadedFileNames, setUploadedFileNames] = useState<string[]>([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<File[]>([]);
  const [previewData, setPreviewData] = useState<
    UploadedDataResponse['data'] | null
  >(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRestoringUploads, setIsRestoringUploads] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeletingUploadedData, setIsDeletingUploadedData] = useState(false);
  const { currentCustomer } = useAppContext();
  const { userProfile } = useAuthContext();
  const queryClient = useQueryClient();
  const customerId = currentCustomer?.customerName;
  const { toastState, showSuccess, showError, hideToast } = useCustomToast();

  const canDeleteUploadedData =
    userProfile?.role === UserRole.ADMIN ||
    userProfile?.customerRole === CustomerRole.ADMIN;

  // Keep the latest callback in a ref so an unstable (inline) prop can't
  // re-trigger the restore effect on every parent render
  const onUploadedDataChangeRef = useRef(onUploadedDataChange);
  useEffect(() => {
    onUploadedDataChangeRef.current = onUploadedDataChange;
  });

  // Restore the customer's latest confirmed upload on mount, so returning to
  // this page (another onboarding step, new browser session) shows the
  // uploaded data instead of the upload box
  useEffect(() => {
    if (!customerId) {
      setIsRestoringUploads(false);
      return;
    }

    let cancelled = false;
    fetchAllUploadedFiles(customerId)
      .then(({ fileIds, fileNames }) => {
        if (cancelled) return;
        onUploadedDataChangeRef.current?.(fileIds.length > 0);
        if (fileIds.length === 0) return;
        setUploadedFileIds(fileIds);
        setUploadedFileNames(fileNames);
        setAnalysisState('completed');
      })
      .catch((error) => {
        // Failing to restore is not critical — fall back to the upload box
        captureException({ error, fn: 'restorePreviousUploads' });
      })
      .finally(() => {
        if (!cancelled) setIsRestoringUploads(false);
      });

    return () => {
      cancelled = true;
    };
  }, [customerId]);

  const handleAnalyze = async (files: File[], fileIds: string[]) => {
    // Check if we have fileIds available
    if (fileIds.length === 0) {
      setAnalysisState('idle');
      return;
    }

    // Store all fileIds and file names
    setUploadedFileIds(fileIds);
    setUploadedFileNames(files.map((file) => file.name));
    // A new upload (re)defines campaigns and KPI columns — drop the cached ones
    if (customerId) invalidateCustomerDataQueries(queryClient, customerId);
    setAnalysisState('completed');
  };

  const handleOpenDeleteDialog = () => setIsDeleteDialogOpen(true);
  const handleCloseDeleteDialog = () => setIsDeleteDialogOpen(false);

  const handleConfirmDeleteUploadedData = async () => {
    if (!customerId) return;

    setIsDeletingUploadedData(true);
    try {
      await customerDataUploadApiService.deleteUploadedFiles(customerId);
      // Campaigns and KPI columns no longer exist — drop the cached ones
      invalidateCustomerDataQueries(queryClient, customerId);
      onUploadedDataChangeRef.current?.(false);
      handleResetToUpload();
      showSuccess('Data Deleted', 'All uploaded data has been deleted.');
    } catch (error) {
      captureException({ error, fn: 'handleConfirmDeleteUploadedData' });
      // The backend refuses with a meaningful message when deletion is not
      // allowed (e.g. models already exist) — show it to the user
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Could not delete uploaded data. Please try again.';
      showError('Delete Failed', message);
    } finally {
      setIsDeleteDialogOpen(false);
      setIsDeletingUploadedData(false);
    }
  };

  const handleResetToUpload = () => {
    setAnalysisState('idle');
    setUploadedFileIds([]);
    setUploadedFileNames([]);
    setIsPreviewMode(false);
    setPreviewFiles([]);
    setPreviewData(null);
  };

  const handlePreviewChart = useCallback(
    (files: File[], data: UploadedDataResponse['data']) => {
      setPreviewFiles(files);
      setPreviewData(data);
      setIsPreviewMode(true);
      setAnalysisState('completed');
    },
    [],
  );

  const handleUploadFromPreview = useCallback(async () => {
    if (!currentCustomer) return;

    // Prevent multiple simultaneous uploads
    if (isUploading) {
      return;
    }

    if (previewFiles.length === 0) {
      return;
    }

    setIsUploading(true);
    setAnalysisState('analyzing');

    try {
      const uploadResults = await Promise.all(
        previewFiles.map(async (file) => {
          try {
            // Get presigned URL
            const presignedResponse =
              await customerDataUploadApiService.getPresignedUrl({
                customerId: currentCustomer.customerName,
                fileName: file.name,
              });

            // Upload to S3
            await customerDataUploadApiService.uploadFileToS3(
              presignedResponse.presignedUrl,
              file,
            );

            // Extract file key from presigned URL
            const url = new URL(presignedResponse.presignedUrl);
            const fileKey = decodeURIComponent(url.pathname.substring(1));

            // Poll for status updates
            const finalProgress =
              await customerDataUploadApiService.pollUploadStatus(
                currentCustomer.customerName,
                fileKey,
              );

            const finalStatus =
              finalProgress.status === 'succeeded' ? 'completed' : 'failed';
            return {
              status: finalStatus,
              fileId:
                finalStatus === 'completed' ? finalProgress.fileId : undefined,
              fileName: file.name,
              errorMessage: finalProgress.errorMessage,
            };
          } catch {
            return {
              status: 'failed' as const,
              fileName: file.name,
              errorMessage: undefined,
            };
          }
        }),
      );

      // Check if any files failed
      const failedFiles = uploadResults.filter(
        (result) => result.status === 'failed',
      );
      const successfulFiles = uploadResults.filter(
        (result) => result.status === 'completed',
      );

      if (failedFiles.length > 0) {
        showError('Upload Error', formatFailedUploadsMessage(failedFiles));

        if (successfulFiles.length === 0) {
          setAnalysisState('idle');
          setIsUploading(false);
          return;
        }
      }

      // Store file IDs and names from successful uploads
      const fileIds = successfulFiles
        .map((result) => result.fileId)
        .filter((id): id is string => id !== undefined);

      const fileNames = successfulFiles.map((result) => result.fileName);

      try {
        const allUploads = await fetchAllUploadedFiles(
          currentCustomer.customerName,
        );
        setUploadedFileIds(
          allUploads.fileIds.length > 0 ? allUploads.fileIds : fileIds,
        );
        setUploadedFileNames(
          allUploads.fileIds.length > 0 ? allUploads.fileNames : fileNames,
        );
      } catch (error) {
        captureException({ error, fn: 'refreshUploadedFilesAfterUpload' });
        setUploadedFileIds(fileIds);
        setUploadedFileNames(fileNames);
      }
      setIsPreviewMode(false);
      setPreviewFiles([]);
      setPreviewData(null);
      // A new upload (re)defines campaigns and KPI columns — drop the cached ones
      invalidateCustomerDataQueries(queryClient, currentCustomer.customerName);
      onUploadedDataChangeRef.current?.(true);

      showSuccess(
        'Upload Complete',
        `Successfully uploaded ${successfulFiles.length} file(s).`,
      );

      // DataVisualization fetches the processed data itself and shows its
      // own loading state until it arrives
      setAnalysisState('completed');
      setIsUploading(false);
    } catch (error) {
      captureException({ error, fn: 'handleUploadFromPreview' });
      showError(
        'Upload Failed',
        'An error occurred during upload. Please try again.',
      );
      setAnalysisState('idle');
      setIsUploading(false);
    }
  }, [
    currentCustomer,
    isUploading,
    previewFiles,
    queryClient,
    showSuccess,
    showError,
  ]);

  const renderContent = () => {
    if (isRestoringUploads) {
      return (
        <Center w="100%" minH="200px">
          <Spinner speed="0.8s" size="xl" thickness="4px" />
        </Center>
      );
    }

    switch (analysisState) {
      case 'analyzing':
        return (
          <AnalyzingSpinner
            message={
              isUploading ? 'Uploading and analyzing...' : 'Analyzing...'
            }
          />
        );

      case 'completed':
        if (isPreviewMode && previewData) {
          // Show preview mode with local data
          return (
            <DataVisualization
              preloadedData={previewData}
              showInternalLoading={false}
              onResetToUpload={handleResetToUpload}
              onUpload={handleUploadFromPreview}
              isPreviewMode={true}
              isUploading={isUploading}
            />
          );
        } else if (uploadedFileIds.length > 0) {
          // Show uploaded data mode
          return (
            <DataVisualization
              fileIds={uploadedFileIds}
              onResetToUpload={handleResetToUpload}
              onDeleteUploadedData={
                canDeleteUploadedData ? handleOpenDeleteDialog : undefined
              }
              uploadedFileNames={uploadedFileNames}
              isPreviewMode={false}
            />
          );
        } else {
          return <Box>No file IDs available</Box>;
        }

      default:
        return (
          <FileUploadBox
            onAnalyze={handleAnalyze}
            onPreviewChart={handlePreviewChart}
          />
        );
    }
  };

  return (
    <Box>
      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        onClose={hideToast}
      />
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeleteUploadedData}
        headerText="Delete uploaded data"
        bodyText="Are you sure you want to delete all uploaded data? This will remove all uploaded files and processed data. This action cannot be undone."
        confirmText="Delete"
        isLoading={isDeletingUploadedData}
      />
      {renderContent()}
    </Box>
  );
};
